import {
	ChevronDownIcon,
	ChevronUpIcon,
	PencilSquareIcon,
	PlusIcon,
	TrashIcon,
	UserIcon,
} from "@heroicons/react/20/solid";
import {
	ArrowPathIcon,
	CheckCircleIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useState,
} from "react";
import CustomButton from "../CustomButton";
import ImageByKey from "../ImageByKey";
import Loader from "../Loader";
import PaginationBar from "./PaginationBar";
import RoundButton from "../RoundButton";
import SearchInputForm from "../SearchInputForm";
import Tooltip from "./Tooltip";
import FilterInput from "./FilterInput";
import {
	Action,
	Column,
	Data,
	DataNotRelational,
	DataRelational,
	SortColumn,
} from "./types";
import clsx from "clsx";

function formatDateTime(date: Date): string {
	const padTo2Digits = (num: number) => num.toString().padStart(2, "0");

	return (
		[
			padTo2Digits(date.getDate()),
			padTo2Digits(date.getMonth() + 1),
			date.getFullYear(),
		].join("/") +
		" " +
		[
			padTo2Digits(date.getHours()),
			padTo2Digits(date.getMinutes()),
			padTo2Digits(date.getSeconds()),
		].join(":")
	);
}

function getValueFromRowData(
	rowData: any,
	column: Column,
	readFileRequest?: (imageKey: string) => Promise<{ url: string }>,
): React.ReactNode {
	let value = rowData;
	const keys = column.key.split(".");

	for (const key of keys) {
		value = value ? value[key] : null;
	}

	if (column.render && column.type === "custom") {
		return column.render(rowData);
	}

	switch (column.type) {
		case "image":
			return (
				<div className="h-11 w-11 flex-shrink-0">
					{readFileRequest && !!value ? (
						<ImageByKey
							className="h-11 w-11 rounded-full object-cover"
							alt=""
							imageKey={column.render ? column.render(rowData) : value || ""}
							readFileRequest={readFileRequest}
						/>
					) : (
						<UserIcon width="100%" height="100%" />
					)}
				</div>
			);
		case "date-time":
			return value
				? formatDateTime(
						new Date(column.render ? column.render(rowData) : value),
					)
				: "-";
		case "date":
			return value
				? formatDateTime(
						new Date(column.render ? column.render(rowData) : value),
					).split(" ")[0]
				: "-";
		case "boolean":
			if ((column.render ? column.render(rowData) : value) === true) {
				return <CheckCircleIcon className="text-green-500 w-10" />;
			} else if ((column.render ? column.render(rowData) : value) === false) {
				return <XMarkIcon className="text-red-500 w-10" />;
			} else {
				return "-";
			}
		case "number":
			return column.render ? column.render(rowData) : value;
		default:
			return value !== null && value !== undefined && value !== ""
				? value
				: "-";
	}
}

interface CustomTableProps {
	data: Record<string, any>[] | any;
	loading: boolean;
	isSearchable: boolean;
	title: string;
	subtitle: string;
	addButtonTitle?: string;
	columns: Column[];
	onCreate?: () => Promise<any>;
	onUpdate?: (rowData: Record<string, any>) => Promise<any>;
	onDelete?: (rowData: Record<string, any>) => Promise<any>;
	onChangeSort?: (sortColumn: SortColumn | null) => Promise<any>;
	onRefresh?: () => Promise<any>;
	editLabel?: string;
	defaultPageSizeOptions?: number[];
	isRelational?: boolean;
	areYouSureLabel?: string;
	areYouSureLabelYes?: string;
	areYouSureLabelNo?: string;
	refresh?: boolean;
	customActions?: Action[];
	readFileRequest?: (imageKey: string) => Promise<{ url: string }>;
	defaultSortKey?: string;
	defaultSortOrder?: "asc" | "desc";
	labelRows?: string;
	onRefreshHint?: string;
	onCreateHint?: string;
	onUpdateHint?: string;
	onDeleteHint?: string;
}

const CustomTable = forwardRef((props: CustomTableProps, ref: any) => {
	const {
		data,
		loading: externalLoading,
		isSearchable,
		columns,
		title,
		subtitle,
		addButtonTitle,
		onCreate,
		onUpdate,
		onDelete,
		editLabel,
		defaultPageSizeOptions,
		isRelational,
		areYouSureLabel,
		areYouSureLabelYes,
		areYouSureLabelNo,
		refresh,
		customActions,
		readFileRequest,
		onRefresh,
		defaultSortKey,
		defaultSortOrder,
		labelRows,
		onRefreshHint,
		onCreateHint,
		onUpdateHint,
		onDeleteHint,
	} = props;

	const [dataObj, setDataObj] = useState<Data>({
		count: 0,
		totalCount: 0,
		results: [],
	});
	const [sortColumn, setSortColumn] = useState<SortColumn>({
		key: defaultSortKey || columns[0].key,
		order: defaultSortOrder || "asc",
	});
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const pageSizeOptions = defaultPageSizeOptions || [5, 10, 20];
	const [pageSize, setPageSize] = useState<number>(pageSizeOptions[0]);
	const [nextEnabled, setNextEnabled] = useState(true);
	const [previousEnabled, setPreviousEnabled] = useState(false);
	const [lastIndex, setLastIndex] = useState<string[]>([]);
	const [errorData, setErrorData] = useState<any>(null);
	const [rowToDoAction, setRowToDoAction] = useState<number | null>(null);
	const [actionToDo, setActionToDo] = useState<string>("");
	const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
	const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
	const [filters, setFilters] = useState<
		{ field: string; operator: string; value: any }[]
	>([]);

	const actions: Record<string, any> = {
		onCreate,
		onUpdate,
		onDelete,
		onRefresh,
	};

	const columnWidth = 90;

	const applyFilters = (result: any) => {
		return Object.entries(filters).every(([key, filterValue]) => {
			if (
				!filterValue ||
				(Array.isArray(filterValue) && filterValue.length === 0)
			)
				return true;
			let value = result;
			const keys = key.split(".");
			for (const k of keys) {
				value = value ? value[k] : null;
			}
			if (Array.isArray(filterValue)) {
				return filterValue.includes(value);
			} else {
				return value
					?.toString()
					.toLowerCase()
					.includes(filterValue.toString().toLowerCase());
			}
		});
	};

	const runData = useCallback(
		(retry = 2) => {
			setIsDataLoading(true);
			if (!externalLoading && Array.isArray(data)) {
				const filteredResults = data.filter((result: any) => {
					const matchesSearch = JSON.stringify(result)
						.toLowerCase()
						.includes(search.toLowerCase());
					const matchesFilters = applyFilters(result);
					return matchesSearch && matchesFilters;
				});
				const sortedResults = filteredResults.sort((a: any, b: any) => {
					const column = columns.find((col) => col.key === sortColumn.key);
					const aValue =
						a[sortColumn.key] || (column?.render && column?.render(a)) || "";
					const bValue =
						b[sortColumn.key] || (column?.render && column?.render(b)) || "";

					if (column?.type === "date" || column?.type === "date-time") {
						return sortColumn.order === "asc"
							? new Date(aValue).getTime() - new Date(bValue).getTime()
							: new Date(bValue).getTime() - new Date(aValue).getTime();
					}

					return sortColumn.order === "asc"
						? aValue.localeCompare(bValue || "")
						: bValue.localeCompare(aValue || "");
				});

				const paginatedResults = sortedResults.slice(
					page * pageSize,
					(page + 1) * pageSize,
				);

				setDataObj({
					count: paginatedResults.length,
					totalCount: sortedResults.length,
					results: paginatedResults,
				});

				setPreviousEnabled(page > 0);
				setNextEnabled(sortedResults.length / pageSize > page + 1);
				setErrorData(null);
				setIsDataLoading(false);
			} else if (!externalLoading && typeof data === "function") {
				if (isRelational) {
					data(
						pageSize,
						page * pageSize,
						sortColumn.key,
						sortColumn.order,
						search,
						filters,
					)
						.then((response: DataRelational) => {
							setDataObj(response);
							setErrorData(null);
							setIsDataLoading(false);
						})
						.catch((error: any) => {
							if (retry > 0) runData(retry - 1);
							else {
								setErrorData(error);
								setIsDataLoading(false);
							}
						});
				} else {
					data(pageSize, page > 0 ? lastIndex[page - 1] : null, search, filters)
						.then((response: DataNotRelational) => {
							const updatedLastIndex = page ? [...lastIndex] : [];
							if (updatedLastIndex.length > page - 1) {
								updatedLastIndex.push(response.lastIndex);
							}
							setLastIndex(updatedLastIndex);
							setNextEnabled(!!response.lastIndex);
							setPreviousEnabled(page > 0);
							setDataObj(response);
							setErrorData(null);
							setIsDataLoading(false);
						})
						.catch((error: any) => {
							if (retry > 0) runData(retry - 1);
							else {
								setErrorData(error);
								setIsDataLoading(false);
							}
						});
				}
			} else {
				setIsDataLoading(false);
			}
		},
		[
			data,
			page,
			pageSize,
			sortColumn,
			search,
			externalLoading,
			isRelational,
			lastIndex,
			columns,
			filters,
		],
	);

	const handleDoAction = useCallback(() => {
		if (actionToDo && rowToDoAction !== null) {
			setIsActionLoading(true);
			actions[actionToDo]?.(dataObj.results[rowToDoAction])
				.then(() => {
					setRowToDoAction(null);
					setActionToDo("");
					setIsActionLoading(false);
					runData();
				})
				.catch((error: any) => {
					setErrorData(error);
					setRowToDoAction(null);
					setActionToDo("");
					setIsActionLoading(false);
					runData();
				});
		}
	}, [actionToDo, rowToDoAction, actions, dataObj.results, runData]);

	const handleCreate = () => {
		setIsActionLoading(true);
		onCreate?.()
			.then(() => {
				setIsActionLoading(false);
				runData();
			})
			.catch((error: any) => {
				setErrorData(error);
				setIsActionLoading(false);
				runData();
			});
	};

	const handleCustomAction = (
		actionFn: (rowData: any) => Promise<void>,
		rowData: any,
	) => {
		return () => {
			setIsActionLoading(true);
			actionFn(rowData)
				.then(() => {
					setIsActionLoading(false);
					runData();
				})
				.catch((error: any) => {
					setErrorData(error);
					setIsActionLoading(false);
					runData();
				});
		};
	};

	useImperativeHandle(ref, () => ({
		refresh: runData,
	}));

	useEffect(() => {
		if (page === 0) {
			runData();
		} else {
			setPage(0);
		}
	}, [search, pageSize, sortColumn, filters]);

	useEffect(() => {
		runData();
	}, [page]);

	useEffect(() => {
		if (Array.isArray(data)) {
			runData();
		}
	}, [data, filters]);

	const renderTableHeader = () => (
		<thead className="w-full border-t border-b py-2 sm:py-4">
			<tr className="w-full flex">
				{[
					...columns,
					...(onUpdate ||
					onDelete ||
					customActions?.some((action) => action.isFreeAction !== false)
						? [{ key: "actions", title: editLabel || "Ações" }]
						: []),
				].map((column) => (
					<th
						key={column.key}
						className={clsx(
							"text-sm text-left",
							"flex flex-col px-1 sm:px-2",
							column.hidden ? "hidden" : "",
						)}
						style={{
							flex: "1 1 0",
							minWidth: columnWidth,
						}}
						scope="col"
					>
						<div className="flex flex-col h-full justify-end gap-1 sm:gap-2">
							{column.useFilter && !column.hidden && (
								<FilterInput
									column={column as any}
									filters={filters as any[]}
									onUpdateFilters={(updatedFilters: any[]) => {
										setFilters(updatedFilters);
									}}
								/>
							)}
							<div
								className="flex items-end cursor-pointer px-1 sm:px-2"
								onClick={() => {
									if (column.sort) {
										setSortColumn({
											key: column.key,
											order: sortColumn.order === "asc" ? "desc" : "asc",
										});
									}
								}}
							>
								{column.title}
								{column.sort && (
									<span>
										{sortColumn.key === column.key ? (
											sortColumn.order === "asc" ? (
												<ChevronUpIcon className="h-5 w-5" aria-hidden="true" />
											) : (
												<ChevronDownIcon
													className="h-5 w-5"
													aria-hidden="true"
												/>
											)
										) : (
											<ChevronDownIcon
												className="h-5 w-5 opacity-50"
												aria-hidden="true"
											/>
										)}
									</span>
								)}
							</div>
						</div>
					</th>
				))}
			</tr>
		</thead>
	);

	const renderTableBody = () => (
		<tbody className="divide-y min-w-full">
			{dataObj.results.map((rowData, index) => (
				<tr
					key={rowData.id || index}
					className={clsx("flex justify-between items-center w-full mt-2")}
				>
					{rowToDoAction === index ? (
						<td
							colSpan={columns.length + 1}
							className="py-2 sm:py-4 w-full px-1 sm:px-2 flex-1"
						>
							<div className="flex justify-between items-center w-full">
								<span>{areYouSureLabel || "Are you sure?"}</span>
								<div className="flex gap-2">
									<CustomButton onClick={handleDoAction}>
										{areYouSureLabelYes || "Yes"}
									</CustomButton>
									<CustomButton onClick={() => setRowToDoAction(null)}>
										{areYouSureLabelNo || "No"}
									</CustomButton>
								</div>
							</div>
						</td>
					) : (
						<>
							{columns.map((column, colIndex) => (
								<td
									key={colIndex}
									className={clsx(
										"text-sm",
										"px-1 sm:px-2 py-2 sm:py-4 text-left break-all",
										"flex flex-row flex-grow",
										column.hidden ? "hidden" : "",
									)}
									style={{
										width: column.width || columnWidth || undefined,
										minWidth: columnWidth,
									}}
								>
									{getValueFromRowData(rowData, column, readFileRequest)}
								</td>
							))}
							{(onUpdate ||
								onDelete ||
								customActions?.some(
									(action) => action.isFreeAction !== false,
								)) && (
								<td
									className={clsx(
										"text-sm",
										"px-1 sm:px-2 py-2 sm:py-4 text-left",
										"flex flex-row flex-grow",
									)}
									style={{
										width: columnWidth,
										minWidth: columnWidth,
									}}
								>
									<div className="flex justify-center items-center gap-2 flex-col lg:flex-row">
										{onUpdate && (
											<RoundButton
												onClick={() => {
													setRowToDoAction(index);
													setActionToDo("onUpdate");
												}}
												className="bg-transparent"
											>
												<Tooltip message={onUpdateHint}>
													<PencilSquareIcon
														className="h-5 w-5"
														aria-hidden="true"
													/>
												</Tooltip>
											</RoundButton>
										)}
										{onDelete && (
											<RoundButton
												onClick={() => {
													setRowToDoAction(index);
													setActionToDo("onDelete");
												}}
												className="bg-transparent"
											>
												<Tooltip message={onDeleteHint}>
													<TrashIcon className="h-5 w-5" aria-hidden="true" />
												</Tooltip>
											</RoundButton>
										)}
										{customActions
											?.filter((action) => action.isFreeAction !== false)
											.map((action, actionIndex) => (
												<RoundButton
													key={actionIndex}
													className={action.buttonClassName}
													onClick={handleCustomAction(action.action, rowData)}
												>
													<Tooltip message={action.hint}>
														{action.icon || action.name}
													</Tooltip>
												</RoundButton>
											))}
									</div>
								</td>
							)}
						</>
					)}
				</tr>
			))}
		</tbody>
	);

	const isLoading = externalLoading || isActionLoading || isDataLoading;

	return (
		<div className="p-10 max-sm:p-4 border-2 rounded-lg w-full">
			<div className="flex flex-col md:flex-row items-center justify-between">
				<div hidden={!title && !subtitle} className="w-full">
					<h2 className="leading-8 text-lg sm:text-xl">{title}</h2>
					<p className="mt-2 sm:mt-0 text-sm sm:text-base">{subtitle}</p>
				</div>
				<div className="flex items-center space-x-4 mt-4 sm:mt-0">
					{isSearchable && (
						<SearchInputForm
							onSubmit={(value) => {
								setSearch(value);
								return Promise.resolve();
							}}
							placeHolder="Pesquisar"
							className="bg-[#232837] w-full sm:w-64 bg-none"
						/>
					)}
					{((ref?.current?.refresh && refresh) || onRefresh) && (
						<RoundButton
							onClick={() => {
								setPage(0);
								if (onRefresh) {
									return onRefresh();
								} else if (ref?.current?.refresh) {
									return ref.current.refresh();
								}
							}}
						>
							<Tooltip message={onRefreshHint}>
								<ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
							</Tooltip>
						</RoundButton>
					)}
					{onCreate && (
						<RoundButton onClick={handleCreate}>
							<Tooltip message={onCreateHint}>
								{addButtonTitle || (
									<PlusIcon className="h-5 w-5" aria-hidden="true" />
								)}
							</Tooltip>
						</RoundButton>
					)}
				</div>
			</div>

			<div className="mt-8 flow-root">
				<div className="-mx-2 -my-2 overflow-x-auto sm:mx-0 lg:-mx-8">
					<div className="inline-block min-w-full py-2 align-middle sm:px-0 lg:px-8">
						<table className="flex w-full flex-col">
							{renderTableHeader()}
							{!errorData && !isLoading && renderTableBody()}
						</table>

						{errorData && !isLoading && (
							<div className="flex w-full h-full justify-center items-center text-center p-10">
								<p>{errorData.message || errorData.toString()}</p>
							</div>
						)}

						{isLoading && (
							<div className="flex w-full h-full justify-center items-center text-center p-10 bg-opacity-10">
								<Loader
									containerProps={{
										className:
											"w-full h-full flex justify-center align-center p-10",
									}}
									width="40px"
									height="40px"
								/>
							</div>
						)}
					</div>
				</div>
				{!errorData && (
					<PaginationBar
						isPageable
						labelShowing="Mostrando"
						labelResults={dataObj.totalCount > 1 ? "resultados" : "resultado"}
						nextEnabled={nextEnabled}
						previousEnabled={previousEnabled}
						nextLabel="Próximo"
						previousLabel="Anterior"
						onNextPage={() => setPage(page + 1)}
						onPreviousPage={() => setPage(Math.max(page - 1, 0))}
						onChangePageSize={setPageSize}
						pageSizeOptions={pageSizeOptions}
						isRelational={isRelational}
						totalCount={
							(dataObj as DataRelational).totalCount || dataObj.totalCount || 0
						}
						labelTo="até"
						labelRows={labelRows}
						page={page}
						labelOf="de"
					/>
				)}
			</div>
		</div>
	);
});

export default CustomTable;
